import {
  ArrowRightIcon,
  ChartBarIcon,
  ChatBubbleLeftRightIcon,
  FolderIcon,
} from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';
import RetentionShell, {
  RetentionRow,
} from '../RetentionShell.component';
import { useCancelSubscriptionContext } from '../CancelSubscription.context';

export type tRetentionZeroLeadsModalProps = {
  isOpen: boolean;
};

/**
 * The opening step for a workspace that has not started yet.
 *
 * It shares RetentionShell with the active-workspace opening so the two cannot drift
 * and so switching between them, once the lead count resolves, is not a visible jump.
 * Only the left column differs: there are no figures worth showing, so the three rows
 * become the next steps the plan is there to enable. Printing "0" three times to
 * someone already on the cancellation screen argues for leaving, not staying.
 */
export default function RetentionZeroLeadsModal(
  props: tRetentionZeroLeadsModalProps,
) {
  const { isOpen } = props;
  const {
    isLoadingLeads,
    handleKeepSubscription,
    handleContinue,
    handleClose,
  } = useCancelSubscriptionContext();
  const { t } = useTranslation('settings/SubscriptionManager');

  const steps = [
    {
      key: 'leads',
      labelKey: 'cancelSubscription.retention.emptyLeads',
      icon: FolderIcon,
    },
    {
      key: 'messages',
      labelKey: 'cancelSubscription.retention.emptyMessages',
      icon: ChatBubbleLeftRightIcon,
    },
    {
      key: 'funnels',
      labelKey: 'cancelSubscription.retention.emptyFunnels',
      icon: ChartBarIcon,
    },
  ];

  return (
    <RetentionShell
      isOpen={isOpen}
      heading={t('cancelSubscription.retention.emptyHeading')}
      intro={t('cancelSubscription.retention.emptyIntro')}
      onKeep={handleKeepSubscription}
      onContinue={handleContinue}
      onClose={handleClose}
      isBusy={isLoadingLeads}
      footnote={t('cancelSubscription.retention.emptyFootnote')}
    >
      {steps.map((step, index) => (
        <RetentionRow
          key={step.key}
          icon={step.icon}
          label={t(step.labelKey)}
          index={index}
          testId={`retention-step-${step.key}`}
          value={
            <ArrowRightIcon
              aria-hidden="true"
              className="h-4 w-4 flex-shrink-0 text-neutral-400"
            />
          }
        />
      ))}
    </RetentionShell>
  );
}
